/**
 * Shared ranking for the two account pickers that ride the global-search endpoint:
 * the work-order Job Location (`AddressTypeahead`) and the accounting `AccountTypeahead`
 * (#438, #582). One implementation so the same query orders the same lots in both.
 */

/** The fields of a search hit that ranking reads. */
export interface RankableAccount {
	accountNumber?: string;
	address?: string;
}

/** Did this row match on the lot itself, rather than only via an owner's name? */
export const matchesLot = (account: RankableAccount, query: string): boolean => {
	const q = query.trim().toLowerCase();
	return (account.accountNumber ?? '').toLowerCase().includes(q)
		|| (account.address ?? '').toLowerCase().includes(q);
};

/**
 * Lot matches first, owner-name-only matches after, each group in server order.
 * Someone who typed part of an address wants that lot, not a namesake.
 */
export const rankLotMatchesFirst = <T extends RankableAccount>(hits: T[], query: string): T[] => [
	...hits.filter((a) => matchesLot(a, query)),
	...hits.filter((a) => !matchesLot(a, query)),
];

/** One row of the accounting typeahead: the full account plus what search knows about it. */
export interface AccountMatch<A> {
	account: A;
	/** Raw comma-separated owner names from search; absent for local-only matches. */
	ownerNames?: string;
}

/** Global-search results, tagged with the query they answer. */
export interface AccountSearchResult {
	query: string;
	hits: Array<RankableAccount & { id: string; ownerNames?: string }>;
}

const normalize = (q: string): string => q.trim().toLowerCase();

/**
 * Merge the accounting picker's two sources into one ordered list.
 *
 * - `accounts` is the flag-gated accounting list: the ONLY source of `balanceCents`
 *   (global search deliberately stopped returning balances) and the candidate pool
 *   (it already honours the caller's `status` filter).
 * - `search` is global-search results — owner-name matching, identical to Cmd+K.
 *
 * Account-number prefix matches lead, computed locally so a fast "7601" + Tab commits
 * the right lot even before the debounced search answers. Search hits follow, joined to
 * `accounts` by id; a hit with no counterpart (e.g. filtered out by status) is dropped.
 *
 * Hits are used ONLY when they answer the text now in the field. While the next search
 * is debouncing or in flight, the last one's hits belong to a different query — and in a
 * Save & Next flow, Tab then Enter would post money to that other account (#582 review).
 */
export const mergeAccountMatches = <A extends { id: string; accountNumber?: string | null }>(
	accounts: A[],
	search: AccountSearchResult | undefined,
	query: string,
	limit = 12,
): Array<AccountMatch<A>> => {
	const q = normalize(query);
	if (!q) return [];
	const hits = search && normalize(search.query) === q ? search.hits : [];
	const ownersById = new Map(hits.map((h) => [h.id, h.ownerNames]));
	const out: Array<AccountMatch<A>> = accounts
		.filter((a) => (a.accountNumber ?? '').toLowerCase().startsWith(q))
		.map((a) => ({ account: a, ownerNames: ownersById.get(a.id) }));
	const seen = new Set(out.map((m) => m.account.id));
	const byId = new Map(accounts.map((a) => [a.id, a]));
	for (const hit of rankLotMatchesFirst(hits, q)) {
		const account = byId.get(hit.id);
		if (!account || seen.has(hit.id)) continue;
		seen.add(hit.id);
		out.push({ account, ownerNames: hit.ownerNames });
	}
	return out.slice(0, limit);
};
